import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import Header from "./Header";
import { User, Category, Project, WorkLogsResponse } from "../types";
import { apiClient } from "../utils/apiClient";

interface WorkLogRow {
  id: number | null;
  category_id: number | string;
  project_id: number | string;
  backlog_url: string;
  note: string;
  hours: number | string;
}

// 入力中の内容を一時保存する下書き（ログイン期限切れ・再読み込み対策）
const DRAFT_KEY_PREFIX = "workLogDraft";
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface WorkLogDraft {
  rows: WorkLogRow[];
  deletedIds: number[];
  savedAt: number;
}

const draftKey = (userId: number, date: string) =>
  `${DRAFT_KEY_PREFIX}:${userId}:${date}`;

const loadDraft = (userId: number, date: string): WorkLogDraft | null => {
  try {
    const raw = localStorage.getItem(draftKey(userId, date));
    if (!raw) return null;
    const draft = JSON.parse(raw) as WorkLogDraft;
    if (
      !Array.isArray(draft.rows) ||
      !Array.isArray(draft.deletedIds) ||
      Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS
    ) {
      localStorage.removeItem(draftKey(userId, date));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

const saveDraft = (
  userId: number,
  date: string,
  rows: WorkLogRow[],
  deletedIds: number[],
) => {
  try {
    const draft: WorkLogDraft = { rows, deletedIds, savedAt: Date.now() };
    localStorage.setItem(draftKey(userId, date), JSON.stringify(draft));
  } catch {
    // localStorage が使えない環境では下書き保存を諦める
  }
};

const clearDraft = (userId: number, date: string) => {
  try {
    localStorage.removeItem(draftKey(userId, date));
  } catch {
    // 何もしない
  }
};

const WorkLogEntry = () => {
  const API_PREFIX = "/api";
  const user = useOutletContext<User | null>();

  const getLocalDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [categories, setCategories] = useState<Category[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [targetDate, setTargetDate] = useState(getLocalDateString(new Date()));
  const [rows, setRows] = useState<WorkLogRow[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  // メッセージ用ステート
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [draftRestored, setDraftRestored] = useState(false);

  // --- State ---
  const [loading, setLoading] = useState(false);

  // --- 締め日判定ロジック ---
  const closingDateStr = user?.current_closing_date || "";
  const isTargetDateClosed = Boolean(
    targetDate && closingDateStr && targetDate <= closingDateStr,
  );

  const createEmptyRow = (): WorkLogRow => ({
    id: null,
    category_id: "",
    project_id: "",
    backlog_url: "",
    note: "",
    hours: 0,
  });

  // fetchWorkLogs を useCallback でラップし、引数で projects を受け取れるように修正
  const fetchWorkLogs = useCallback(
    async (date: string, currentProjects: Project[]) => {
      if (!user || currentProjects.length === 0) return;

      setLoading(true); // 開始時にON
      try {
        // 未保存の下書きがあれば、サーバーのデータより優先して復元する
        const draft = loadDraft(user.id, date);
        if (draft) {
          setRows(draft.rows);
          setDeletedIds(draft.deletedIds);
          setDraftRestored(true);
          return;
        }
        setDraftRestored(false);

        const data = await apiClient.get<WorkLogsResponse>(
          `${API_PREFIX}/work_logs?start_date=${date}&end_date=${date}&user_id=${user.id}`,
        );

        const logs = data.work_logs || [];
        const formattedRows: WorkLogRow[] = logs.map((log: any) => {
          const project = currentProjects.find((p) => p.id === log.project_id);
          return {
            id: log.id,
            category_id: project ? project.category_id : "",
            project_id: log.project_id,
            backlog_url: log.backlog_url || "",
            note: log.note || "",
            hours: log.hours,
          };
        });

        formattedRows.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        if (formattedRows.length === 0) {
          formattedRows.push(createEmptyRow());
        }

        setRows(formattedRows);
        setDeletedIds([]);
      } catch (err) {
        console.error("WorkLogs fetch error:", err);
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  // 1. マスタデータの取得 (コンポーネントマウント時、user取得後に1回だけ実行)
  useEffect(() => {
    if (!user) return; // userがいない時は何もしない

    const initMasterData = async () => {
      try {
        const [catData, prjData] = await Promise.all([
          apiClient.get<Category[]>(`${API_PREFIX}/categories`),
          apiClient.get<Project[]>(`${API_PREFIX}/projects`),
        ]);

        setCategories(catData);
        setProjects(prjData);
      } catch (err) {
        console.error("Master data fetch error:", err);
      }
    };

    initMasterData();
  }, [user]);

  // 2. 業務報告ログの取得 (日付が変わった時、またはマスタ取得が完了した時に実行)
  useEffect(() => {
    // マスタデータ(projects)の読み込みが終わっていない場合はAPIを叩かない
    if (projects.length === 0) return;

    fetchWorkLogs(targetDate, projects);
  }, [targetDate, projects, fetchWorkLogs]);

  const handleRowChange = (
    index: number,
    field: keyof WorkLogRow,
    value: any,
  ) => {
    const newRows = [...rows];
    (newRows[index] as any)[field] = value;

    if (field === "category_id") {
      newRows[index]["project_id"] = "";
    }
    setRows(newRows);
    if (user) saveDraft(user.id, targetDate, newRows, deletedIds);
  };

  // 行追加・削除
  const addRow = () => {
    const newRows = [...rows, createEmptyRow()];
    setRows(newRows);
    if (user) saveDraft(user.id, targetDate, newRows, deletedIds);
  };

  const removeRow = (index: number) => {
    const row = rows[index];
    const newDeletedIds = row.id ? [...deletedIds, row.id] : deletedIds;
    const newRows = rows.filter((_, i) => i !== index);
    setDeletedIds(newDeletedIds);
    setRows(newRows);
    if (user) saveDraft(user.id, targetDate, newRows, newDeletedIds);
  };

  const handleSave = async () => {
    if (!user) return;
    setSuccessMessage(null);
    setErrorMessages([]);

    // バリデーションチェック
    const invalidRows = rows.filter((row) => {
      // 完全に空の行は無視
      const isEmpty =
        !row.id &&
        !row.project_id &&
        !row.hours &&
        !row.note &&
        !row.backlog_url;
      if (isEmpty) return false;

      // 必須項目チェック
      return !row.project_id || !row.hours;
    });

    if (invalidRows.length > 0) {
      setErrorMessages(["プロジェクトと作業時間は必須です。"]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const activeLogs = rows
      .filter((row) => row.project_id && row.hours)
      .map((row) => ({
        id: row.id,
        project_id: row.project_id,
        hours: row.hours,
        note: row.note,
        backlog_url: row.backlog_url,
        worked_on: targetDate,
      }));

    const payload = {
      user: {
        work_logs_attributes: [
          ...activeLogs,
          ...deletedIds.map((id) => ({ id, _destroy: true })),
        ],
      },
    };

    try {
      await apiClient.patch<void>(
        `${API_PREFIX}/work_logs/batch_update`,
        payload,
        // 期限切れ時は、入力内容が下書きとして残ることを伝えてからログイン画面へ遷移する
        {
          unauthorizedMessage:
            "ログインの有効期限が切れました。入力内容は保存されています。再ログイン後、業務報告登録を開くと復元されます。",
        },
      );
      clearDraft(user.id, targetDate);
      setDraftRestored(false);
      setSuccessMessage("更新しました！");
      fetchWorkLogs(targetDate, projects);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      const responseErrors = error.response?.data;
      if (responseErrors && typeof responseErrors === "object") {
        const msgs = Object.values(responseErrors).flat() as string[];
        setErrorMessages(
          msgs.length > 0 ? msgs : ["保存エラーが発生しました。"],
        );
      } else {
        setErrorMessages([error.message || "保存エラー"]);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const totalHours = rows.reduce((sum, row) => sum + Number(row.hours || 0), 0);

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}
    >
      <Header title="業務報告登録" />
      <div className="limit-width px-4">
        {successMessage && (
          <div className="alert alert-success mb-3 animate__animated animate__fadeIn">
            ✅ {successMessage}
          </div>
        )}
        {draftRestored && (
          <div className="alert alert-info mb-3">
            未保存の入力内容を復元しました。「更新」を押すと保存されます。
          </div>
        )}
        {errorMessages.length > 0 && (
          <div className="alert alert-danger mb-3 animate__animated animate__fadeIn">
            <ul className="mb-0 ps-3">
              {errorMessages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="row mb-3 align-items-center bg-white p-2 border rounded shadow-sm mx-0">
          <div className="col-auto">
            <input
              type="date"
              className="form-control fw-bold border-0"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
          </div>

          {/* ローディング表示 */}
          {loading && (
            <div className="col-auto align-self-center text-muted small">
              <span className="spinner-border spinner-border-sm me-1"></span>
              読込中...
            </div>
          )}

          <div className="col text-end fw-bold">
            作業合計： <span className="text-primary fs-5">{totalHours}</span>{" "}
            時間
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-primary text-white row g-0 text-center fw-bold border-0 py-3">
            <div className="col-2">カテゴリ</div>
            <div className="col-2">プロジェクト名</div>
            <div className="col-2">Backlog URL</div>
            <div className="col-4">作業メモ</div>
            <div className="col-2">作業時間</div>
          </div>

          <div className="card-body p-2 bg-white">
            {rows.map((row, index) => {
              const selectedProject = projects.find(
                (p) => p.id === Number(row.project_id),
              );

              // 判定の修正：既存レコード(row.idあり)かつ締め済、またはプロジェクト終了の場合に無効化
              const isProjectEnded = Boolean(
                selectedProject && !selectedProject.active,
              );
              const isDisabled = Boolean(
                isProjectEnded || (row.id && isTargetDateClosed),
              );

              // バッジ表示ロジック：既存レコードの場合のみ「締切済」を出す
              let badgeText = null;
              if (row.id && isTargetDateClosed) {
                badgeText = "🔒 締切済";
              } else if (isProjectEnded) {
                badgeText = "🔒 終了P";
              }

              const filteredProjects = projects.filter(
                (p) =>
                  p.category_id === Number(row.category_id) &&
                  (p.active || p.id === Number(row.project_id)),
              );

              return (
                <div
                  key={index}
                  className={`row g-2 align-items-center mb-2 border-bottom pb-2 ${isDisabled ? "bg-light opacity-75" : ""}`}
                >
                  <div className="col-2">
                    <select
                      className="form-select"
                      value={row.category_id}
                      onChange={(e) =>
                        handleRowChange(index, "category_id", e.target.value)
                      }
                      disabled={isDisabled}
                    >
                      <option value="">選択</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-2">
                    <select
                      className={`form-select ${selectedProject && !selectedProject.active ? "is-invalid" : ""}`}
                      value={row.project_id}
                      onChange={(e) =>
                        handleRowChange(index, "project_id", e.target.value)
                      }
                      disabled={!row.category_id || isDisabled}
                    >
                      <option value="">選択してください</option>
                      {filteredProjects.map((p) => (
                        <option
                          key={p.id}
                          value={p.id}
                          title={p.description || undefined}
                        >
                          {p.name}
                          {!p.active ? " (終了)" : ""}
                        </option>
                      ))}
                    </select>
                    {selectedProject && !selectedProject.active && (
                      <div
                        className="text-danger small mt-1"
                        style={{ fontSize: "0.75rem" }}
                      >
                        ※終了したプロジェクトは修正できません
                      </div>
                    )}
                  </div>
                  <div className="col-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="https://..."
                      value={row.backlog_url}
                      onChange={(e) =>
                        handleRowChange(index, "backlog_url", e.target.value)
                      }
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="col-4">
                    <textarea
                      className="form-control"
                      placeholder="作業メモ"
                      value={row.note}
                      onChange={(e) =>
                        handleRowChange(index, "note", e.target.value)
                      }
                      rows={1}
                      disabled={isDisabled}
                      style={{ minHeight: "38px" }}
                    />
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-center gap-2">
                    <input
                      type="number"
                      className="form-control text-end"
                      style={{ width: "70px" }}
                      step="0.5"
                      value={row.hours}
                      onChange={(e) =>
                        handleRowChange(index, "hours", e.target.value)
                      }
                      disabled={isDisabled}
                    />

                    {badgeText && row.id ? (
                      <span
                        className="badge bg-secondary shadow-sm text-nowrap px-2 py-1"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {badgeText}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm rounded-circle btn-outline-danger"
                        onClick={() => removeRow(index)}
                        disabled={isDisabled}
                        title="削除"
                      >
                        削除
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="text-center py-3">
              <button
                type="button"
                className="btn btn-outline-primary px-4 rounded-pill fw-bold"
                onClick={addRow}
                // disabled={isTargetDateClosed} を削除して新規追加を許可
              >
                ＋ 行を追加
              </button>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column align-items-end mt-4 mb-5">
          <button
            type="button"
            className="btn btn-primary px-5 py-2 shadow fw-bold"
            onClick={handleSave}
            // disabled={isTargetDateClosed} を削除して保存を許可
          >
            更新
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkLogEntry;
